import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Folder,
  Building,
  BarChart3,
  Map,
  MapPin,
  TrendingUp,
} from "lucide-react";
import { DashboardTable } from "./DashboardTable";
import {
  useDashboardCategories,
  useDashboardTypes,
  useDashboardSegments,
  useDashboardRegions,
  useDashboardLocalities,
} from "../hooks/useDashboardData";
import { normalizeTableData } from "../utils/dataMapper";
import {
  isOfficialSource,
  officialOnlyFilters,
  hasWithoutData,
  withoutDataTotals,
  WITHOUT_DATA_COLUMNS,
} from "../utils/statsSource.utils";
import type {
  DashboardFilters,
  DashboardResponse,
  DashboardTabType,
} from "../types/dashboard.types";

interface DashboardTabsProps {
  filters?: DashboardFilters;
  activeTab: DashboardTabType;
  onTabChange: (tab: DashboardTabType) => void;
}

export const DashboardTabs: React.FC<DashboardTabsProps> = ({
  filters,
  activeTab,
  onTabChange,
}) => {
  // Categories, types and segments are official-only (pre-registrations have no classification)
  const officialFilters = officialOnlyFilters(filters);
  const showOfficialOnlyNote = !isOfficialSource(filters?.source);

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useDashboardCategories(officialFilters);
  const {
    data: typesData,
    isLoading: typesLoading,
    error: typesError,
  } = useDashboardTypes(officialFilters);
  const {
    data: segmentsData,
    isLoading: segmentsLoading,
    error: segmentsError,
  } = useDashboardSegments(officialFilters);
  const {
    data: regionsData,
    isLoading: regionsLoading,
    error: regionsError,
  } = useDashboardRegions(filters);
  const {
    data: localitiesData,
    isLoading: localitiesLoading,
    error: localitiesError,
  } = useDashboardLocalities(filters);

  // Debug logging to see actual data structure (can be removed in production)
  React.useEffect(() => {
    if (categoriesData && process.env.NODE_ENV === "development") {
      console.log(
        "🔍 Categories Data Structure:",
        JSON.stringify(categoriesData, null, 2)
      );
      console.log("🔍 First category item:", categoriesData.data?.[0]);
    }
  }, [categoriesData]);

  React.useEffect(() => {
    if (segmentsData && process.env.NODE_ENV === "development") {
      console.log(
        "🔍 Segments Data Structure:",
        JSON.stringify(segmentsData, null, 2)
      );
      console.log("🔍 First segment item:", segmentsData.data?.[0]);
    }
  }, [segmentsData]);

  const categoryColumns = [
    { key: "seg", label: "Seg" },
    { key: "categorias", label: "Categorías" },
    { key: "alojamientos", label: "Alojamientos" },
    { key: "plazas", label: "Plazas" },
    { key: "habitaciones", label: "Habitaciones" },
    { key: "a%", label: "A %", className: "text-blue-600" },
    { key: "p%", label: "P %", className: "text-blue-600" },
    { key: "h%", label: "H %", className: "text-blue-600" },
  ];

  const typeColumns = [
    { key: "seg", label: "Seg" },
    { key: "tipo", label: "Tipo" },
    { key: "alojamientos", label: "Alojamientos" },
    { key: "plazas", label: "Plazas" },
    { key: "habitaciones", label: "Habitaciones" },
    { key: "a%", label: "A%", className: "text-blue-600" },
    { key: "p%", label: "P%", className: "text-blue-600" },
    { key: "h%", label: "H%", className: "text-blue-600" },
  ];

  const segmentColumns = [
    { key: "segmento", label: "Segmento" },
    { key: "alojamientos", label: "Alojamientos" },
    { key: "plazas", label: "Plazas" },
    { key: "habitaciones", label: "Habitaciones" },
    { key: "a%", label: "A%", className: "text-blue-600" },
    { key: "p%", label: "P%", className: "text-blue-600" },
    { key: "h%", label: "H%", className: "text-blue-600" },
  ];

  const regionColumns = [
    { key: "regiones", label: "Regiones" },
    { key: "alojamientos", label: "Alojamientos" },
    { key: "plazas", label: "Plazas" },
    { key: "habitaciones", label: "Habitaciones" },
    { key: "a%", label: "A%", className: "text-blue-600" },
    { key: "p%", label: "P%", className: "text-blue-600" },
    { key: "h%", label: "H%", className: "text-blue-600" },
  ];

  const localityColumns = [
    { key: "localidad", label: "Localidad" },
    { key: "alojamientos", label: "Alojamientos" },
    { key: "plazas", label: "Plazas" },
    { key: "habitaciones", label: "Habitaciones" },
    { key: "a%", label: "A%", className: "text-blue-600" },
    { key: "p%", label: "P%", className: "text-blue-600" },
    { key: "h%", label: "H%", className: "text-blue-600" },
  ];

  const officialOnlyBadge = (
    <span className="hidden lg:inline text-xs text-amber-700">(solo oficiales)</span>
  );

  const officialOnlyNote = (
    <p className="mb-3 text-sm text-amber-700">
      Solo alojamientos oficiales: los preinscriptos no tienen categoría, tipo ni segmento.
    </p>
  );

  // Regions and localities add "Sin dato" columns when the source is not official
  const withWithoutData = (
    columns: { key: string; label: string; className?: string }[],
    response?: DashboardResponse<unknown>
  ) => (hasWithoutData(response) ? [...columns, ...WITHOUT_DATA_COLUMNS] : columns);

  const getTotals = (
    data: Record<string, unknown> | undefined
  ): Record<string, unknown> | undefined => {
    if (!data) return undefined;

    // Calculate totals from the data array if main totals aren't available
    let totalCount = 0;
    let totalPlaces = 0;
    let totalRooms = 0;

    if (data.data && Array.isArray(data.data)) {
      data.data.forEach((item: Record<string, unknown>) => {
        totalCount += (item.count as number) || 0;
        totalPlaces += (item.totalPlaces as number) || 0;
        totalRooms += (item.totalRooms as number) || 0;
      });
    }

    return {
      alojamientos: (data.count as number) || totalCount,
      plazas: (data.totalPlaces as number) || totalPlaces,
      habitaciones: (data.totalRooms as number) || totalRooms,
      "a%": 1.0, // 100% for totals
      "p%": 1.0, // 100% for totals
      "h%": 1.0, // 100% for totals
    };
  };

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => onTabChange(value as DashboardTabType)}
    >
      <TabsList className="grid w-full grid-cols-6 mb-6">
        <TabsTrigger value="categories" className="flex items-center space-x-2">
          <Folder className="w-4 h-4" />
          <span className="hidden sm:inline">Por Categorías</span>
          {showOfficialOnlyNote && officialOnlyBadge}
        </TabsTrigger>
        <TabsTrigger value="types" className="flex items-center space-x-2">
          <Building className="w-4 h-4" />
          <span className="hidden sm:inline">Por Tipos</span>
          {showOfficialOnlyNote && officialOnlyBadge}
        </TabsTrigger>
        <TabsTrigger value="segments" className="flex items-center space-x-2">
          <BarChart3 className="w-4 h-4" />
          <span className="hidden sm:inline">Por Segmentos</span>
          {showOfficialOnlyNote && officialOnlyBadge}
        </TabsTrigger>
        <TabsTrigger value="regions" className="flex items-center space-x-2">
          <Map className="w-4 h-4" />
          <span className="hidden sm:inline">Por Regiones</span>
        </TabsTrigger>
        <TabsTrigger value="localities" className="flex items-center space-x-2">
          <MapPin className="w-4 h-4" />
          <span className="hidden sm:inline">Por Localidades</span>
        </TabsTrigger>
        <TabsTrigger
          value="visualizations"
          className="flex items-center space-x-2"
        >
          <TrendingUp className="w-4 h-4" />
          <span className="hidden sm:inline">Visualizaciones</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="categories">
        {showOfficialOnlyNote && officialOnlyNote}
        <DashboardTable
          title="Distribución por Categorías y Tipos"
          columns={categoryColumns}
          data={normalizeTableData(
            (categoriesData?.data || []) as unknown as Record<
              string,
              unknown
            >[],
            "categories",
            getTotals(
              categoriesData as unknown as Record<string, unknown> | undefined
            )
          )}
          isLoading={categoriesLoading}
          error={categoriesError}
          totals={getTotals(
            categoriesData as unknown as Record<string, unknown> | undefined
          )}
          exportEndpoint="by-categories"
          filters={officialFilters}
        />
      </TabsContent>

      <TabsContent value="types">
        {showOfficialOnlyNote && officialOnlyNote}
        <DashboardTable
          title="Distribución por Tipos de Alojamiento"
          columns={typeColumns}
          data={normalizeTableData(
            (typesData?.data || []) as unknown as Record<string, unknown>[],
            "types",
            getTotals(
              typesData as unknown as Record<string, unknown> | undefined
            )
          )}
          isLoading={typesLoading}
          error={typesError}
          totals={getTotals(
            typesData as unknown as Record<string, unknown> | undefined
          )}
          exportEndpoint="by-types"
          filters={officialFilters}
        />
      </TabsContent>

      <TabsContent value="segments">
        {showOfficialOnlyNote && officialOnlyNote}
        <DashboardTable
          title="Distribución por Segmentos"
          columns={segmentColumns}
          data={normalizeTableData(
            (segmentsData?.data || []) as unknown as Record<string, unknown>[],
            "segments",
            getTotals(
              segmentsData as unknown as Record<string, unknown> | undefined
            )
          )}
          isLoading={segmentsLoading}
          error={segmentsError}
          totals={getTotals(
            segmentsData as unknown as Record<string, unknown> | undefined
          )}
          exportEndpoint="by-segments"
          filters={officialFilters}
        />
      </TabsContent>

      <TabsContent value="regions">
        <DashboardTable
          title="Distribución por Regiones"
          columns={withWithoutData(regionColumns, regionsData)}
          data={normalizeTableData(
            (regionsData?.data || []) as unknown as Record<string, unknown>[],
            "regions",
            getTotals(
              regionsData as unknown as Record<string, unknown> | undefined
            )
          )}
          isLoading={regionsLoading}
          error={regionsError}
          totals={{
            ...getTotals(
              regionsData as unknown as Record<string, unknown> | undefined
            ),
            ...withoutDataTotals(regionsData),
          }}
          exportEndpoint="by-regions"
          filters={filters}
        />
      </TabsContent>

      <TabsContent value="localities">
        <DashboardTable
          title="Distribución por Localidades"
          columns={withWithoutData(localityColumns, localitiesData)}
          data={normalizeTableData(
            (localitiesData?.data || []) as unknown as Record<
              string,
              unknown
            >[],
            "localities",
            getTotals(
              localitiesData as unknown as Record<string, unknown> | undefined
            )
          )}
          isLoading={localitiesLoading}
          error={localitiesError}
          totals={{
            ...getTotals(
              localitiesData as unknown as Record<string, unknown> | undefined
            ),
            ...withoutDataTotals(localitiesData),
          }}
          exportEndpoint="by-localities"
          filters={filters}
        />
      </TabsContent>

      <TabsContent value="visualizations">
        <div className="text-center py-12 text-gray-500">
          <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold mb-2">Visualizaciones</h3>
          <p>Las visualizaciones gráficas estarán disponibles próximamente.</p>
        </div>
      </TabsContent>
    </Tabs>
  );
};
